import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Bot, User, Mic, Square } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatPanel = ({ mode, suggestions, testidPrefix }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const sidRef = useRef(null);
  const scrollRef = useRef(null);
  const recRef = useRef(null);

  const toggleRecording = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        try {
          const res = await fetch(`${API}/stt`, { method: "POST", body: fd });
          const data = await res.json();
          const text = (data.text || "").trim();
          if (text) {
            send(text);
          } else {
            toast.error("Couldn't hear anything — try again, closer to the mic.");
          }
        } catch {
          toast.error("Transcription failed — try typing instead.");
        }
      };
      recRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied — allow it in your browser bar.");
    }
  };

  useEffect(() => {
    let sid = localStorage.getItem(`az_sid_${mode}`);
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem(`az_sid_${mode}`, sid);
    }
    sidRef.current = sid;
    fetch(`${API}/chat/history/${sid}?mode=${mode}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [mode]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [messages]);

  const send = async (preset) => {
    const content = (preset ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: content }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: content, mode }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            acc += JSON.parse(payload);
          } catch {}
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: acc };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connection hiccup — try again in a second." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid={`${testidPrefix}-panel`}>
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid={`${testidPrefix}-messages`}>
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <Sparkles size={22} className="text-violet-400" />
            <p className="font-mono text-xs text-slate-500">start the conversation — pick a prompt or type below</p>
            <div className="flex max-w-md flex-wrap justify-center gap-2 pt-2">
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  data-testid={`${testidPrefix}-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] text-slate-400 transition-all duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300">
                <Bot size={13} />
              </span>
            )}
            <div
              data-testid={`${testidPrefix}-message-${m.role}-${i}`}
              className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                  ? "rounded-br-sm bg-violet-600/80 text-white"
                  : "rounded-bl-sm border border-white/10 bg-white/5 text-slate-200"
              }`}
            >
              {m.text || (streaming && i === messages.length - 1 ? "▍" : "")}
            </div>
            {m.role === "user" && (
              <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-300">
                <User size={13} />
              </span>
            )}
          </div>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="flex items-center gap-2 border-t border-white/10 p-3"
      >
        <input
          data-testid={`${testidPrefix}-input`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={recording ? "Listening… tap the red button to stop" : "Type or speak your message..."}
          className={`flex-1 rounded-full border bg-white/5 px-4 py-2.5 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-cyan-400/60 ${
            recording ? "border-rose-500/50" : "border-white/10"
          }`}
        />
        <button
          data-testid={`${testidPrefix}-mic-button`}
          type="button"
          onClick={toggleRecording}
          disabled={streaming}
          title={recording ? "Stop & transcribe" : "Voice input (Whisper)"}
          aria-label={recording ? "Stop recording" : "Start voice input"}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 disabled:opacity-40 ${
            recording
              ? "animate-pulse border-rose-500/60 bg-rose-500/20 text-rose-400"
              : "border-white/10 text-slate-400 hover:border-cyan-400/50 hover:text-cyan-300"
          }`}
        >
          {recording ? <Square size={13} /> : <Mic size={15} />}
        </button>
        <button
          data-testid={`${testidPrefix}-send-button`}
          type="submit"
          disabled={streaming || !input.trim()}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white transition-all duration-300 hover:opacity-90 disabled:opacity-40"
          aria-label="Send message"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
};

const ASSISTANT_SUGGESTIONS = [
  "What are Ahmed's top skills?",
  "Tell me about the Dividend Derivative project",
  "Is Ahmed open to work?",
  "What's his education background?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <motion.button
        data-testid="ai-chat-fab"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2.2, type: "spring", stiffness: 200, damping: 16 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[85] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-violet-600 text-white shadow-[0_8px_40px_rgba(139,92,246,0.45)]"
        aria-label="Open AI assistant"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="glass fixed bottom-24 right-6 z-[85] flex h-[440px] w-[calc(100vw-3rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-violet-500/30 shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
            data-testid="ai-chat-widget"
          >
            <div className="border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse-dot rounded-full bg-emerald-400" />
                <span className="font-display text-sm font-bold text-slate-100">AZ Assistant</span>
              </div>
              <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-500">
                gpt-5.4-mini · knows Ahmed's work
              </p>
            </div>
            <div className="min-h-0 flex-1">
              <ChatPanel mode="assistant" suggestions={ASSISTANT_SUGGESTIONS} testidPrefix="widget" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
